import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { fetchServerMainBundleId, getLoadedMainBundleId } from '../utils/bundleVersion';

const VERSION_CHECK_INTERVAL = 60000; // Check every minute
// Keep retrying auto-refresh for this long / this many times before giving up.
// Prevents an endless reload loop when a refresh fails to converge on the new bundle.
const REFRESH_ATTEMPT_KEY = 'app_version_refresh_attempt';
const MAX_REFRESH_ATTEMPTS = 5;
const REFRESH_RETRY_WINDOW_MS = 5 * 60 * 1000; // 5 minutes

function getRefreshAttempt() {
  try {
    const raw = sessionStorage.getItem(REFRESH_ATTEMPT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      !parsed ||
      typeof parsed.bundleId !== 'string' ||
      typeof parsed.attempts !== 'number' ||
      typeof parsed.firstAttemptAt !== 'number'
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function setRefreshAttempt(record) {
  try {
    sessionStorage.setItem(REFRESH_ATTEMPT_KEY, JSON.stringify(record));
  } catch {
    // Ignore storage errors; worst case we lose loop protection for this tab.
  }
}

function clearRefreshAttempt() {
  try {
    sessionStorage.removeItem(REFRESH_ATTEMPT_KEY);
  } catch {
    // Ignore storage errors.
  }
}

function shouldGiveUpRefresh(serverBundleId) {
  const attempt = getRefreshAttempt();
  if (!attempt || attempt.bundleId !== serverBundleId) {
    return false;
  }

  const elapsed = Date.now() - attempt.firstAttemptAt;
  return (
    attempt.attempts >= MAX_REFRESH_ATTEMPTS ||
    elapsed >= REFRESH_RETRY_WINDOW_MS
  );
}

function recordRefreshAttempt(serverBundleId) {
  const existing = getRefreshAttempt();
  const now = Date.now();

  if (existing && existing.bundleId === serverBundleId) {
    setRefreshAttempt({
      bundleId: serverBundleId,
      attempts: existing.attempts + 1,
      firstAttemptAt: existing.firstAttemptAt,
    });
    return;
  }

  // New target bundle (or first attempt): start a fresh retry window.
  setRefreshAttempt({
    bundleId: serverBundleId,
    attempts: 1,
    firstAttemptAt: now,
  });
}

async function hardRefresh() {
  if ('caches' in window) {
    const names = await caches.keys();
    await Promise.all(names.map((name) => caches.delete(name)));
  }

  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
  }

  const url = new URL(window.location.href);
  url.searchParams.set('_refresh', Date.now().toString());
  window.location.href = url.toString();
}

const VersionUpdater = () => {
  const { isAuthenticated } = useAuth();
  const isRefreshingRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const justRefreshed = urlParams.has('_refresh');

    if (justRefreshed) {
      const newUrl = window.location.pathname + (window.location.hash || '');
      window.history.replaceState({}, '', newUrl);
    }

    const checkBundleVersion = async ({ suppressAfterRefresh = false } = {}) => {
      if (isRefreshingRef.current) {
        return;
      }

      try {
        const [serverBundleId, loadedBundleId] = await Promise.all([
          fetchServerMainBundleId(),
          Promise.resolve(getLoadedMainBundleId()),
        ]);

        if (!serverBundleId || !loadedBundleId) {
          return;
        }

        if (serverBundleId === loadedBundleId) {
          // Converged on the latest bundle; clear any prior refresh attempt.
          clearRefreshAttempt();
          return;
        }

        if (suppressAfterRefresh) {
          return;
        }

        // Durable guard: after several failed refreshes toward the same server
        // bundle (or once the retry window expires), stop to avoid an infinite loop.
        if (shouldGiveUpRefresh(serverBundleId)) {
          console.warn(
            '[VersionUpdater] Bundle still stale after refresh retries; suppressing further auto-reloads for',
            serverBundleId
          );
          return;
        }

        isRefreshingRef.current = true;
        recordRefreshAttempt(serverBundleId);
        await hardRefresh();
      } catch (error) {
        console.warn('[VersionUpdater] Error checking bundle version:', error);
        isRefreshingRef.current = false;
      }
    };

    const runInitialCheck = () => {
      checkBundleVersion({ suppressAfterRefresh: justRefreshed });
    };

    const checkVersion = () => {
      if (document.readyState === 'complete') {
        runInitialCheck();
      } else {
        window.addEventListener('load', runInitialCheck, { once: true });
      }
    };

    if (document.readyState === 'loading') {
      window.addEventListener('load', checkVersion, { once: true });
    } else {
      const delay = justRefreshed ? 500 : 100;
      setTimeout(checkVersion, delay);
    }

    const interval = setInterval(checkBundleVersion, VERSION_CHECK_INTERVAL);

    return () => {
      clearInterval(interval);
      window.removeEventListener('load', checkVersion);
      window.removeEventListener('load', runInitialCheck);
    };
  }, [isAuthenticated]);

  return null;
};

export default VersionUpdater;
